import { useLang } from "../i18n";
import { useSEO } from "../seo";
import { FadeIn, ParallaxImage } from "../components/Reveal";
import { CTAButton, PageHero, SectionLabel } from "../components/Bits";

const IMG = {
  hero: "https://images.pexels.com/photos/6585598/pexels-photo-6585598.jpeg?auto=compress&cs=tinysrgb&w=2000",
  riqualificazione: "https://images.unsplash.com/photo-1600566753086-00f18fb6b3ea?q=80&w=1400&auto=format&fit=crop",
  frazionamento: "https://images.unsplash.com/photo-1600566752355-35792bedcfea?q=80&w=1400&auto=format&fit=crop",
  cambioUso: "https://images.unsplash.com/photo-1449157291145-7efd050a4d0e?q=80&w=1400&auto=format&fit=crop",
};

const copy = {
  it: {
    seoTitle: "Valorizzazione Immobiliare | Nicole Srl",
    seoDesc: "Riqualificazione, frazionamento e cambio di destinazione d'uso: come Nicole Srl costruisce il valore di un immobile.",
    kicker: "Valorizzazione",
    lines: ["Il valore non si trova.", "Si costruisce."],
    sub: "Ristrutturazioni, frazionamenti, cambi di destinazione d'uso: ogni intervento nasce dall'analisi del potenziale e mira al miglior utilizzo possibile dell'immobile.",
    label: "Le tre leve",
    areas: [
      { n: "01", title: "Riqualificazione", text: "Trasformazione fisica e qualitativa dell'immobile: distribuzione degli spazi, impianti, materiali, illuminazione e finiture studiati per il mercato finale.", img: IMG.riqualificazione, alt: "Interno riqualificato con materiali contemporanei" },
      { n: "02", title: "Frazionamento", text: "Riorganizzazione di un immobile in più unità, quando tecnicamente e urbanisticamente possibile, per esprimere al meglio il valore degli spazi.", img: IMG.frazionamento, alt: "Dettaglio di interni dopo frazionamento" },
      { n: "03", title: "Cambio d'uso", text: "Studio di nuove funzioni capaci di esprimere maggiormente il potenziale dell'immobile, nel rispetto della normativa urbanistica ed edilizia applicabile.", img: IMG.cambioUso, alt: "Edificio con nuova destinazione d'uso" },
    ],
    conclusion: "Ogni trasformazione parte dalla stessa domanda: qual è il miglior utilizzo possibile di questo immobile?",
    cta: "Richiedi una valutazione",
  },
  en: {
    seoTitle: "Real Estate Value Creation | Nicole Srl",
    seoDesc: "Redevelopment, subdivision and change of use: how Nicole Srl builds a property's value.",
    kicker: "Value creation",
    lines: ["Value is not found.", "It is built."],
    sub: "Renovations, subdivisions, changes of use: every intervention starts from analysing the potential and aims at the best possible use of the property.",
    label: "The three levers",
    areas: [
      { n: "01", title: "Redevelopment", text: "Physical and qualitative transformation of the property: layout, systems, materials, lighting and finishes designed for the final market.", img: IMG.riqualificazione, alt: "Redeveloped interior with contemporary materials" },
      { n: "02", title: "Subdivision", text: "Reorganising a property into multiple units, where technically and legally feasible, to best express the value of the space.", img: IMG.frazionamento, alt: "Interior detail after subdivision" },
      { n: "03", title: "Change of use", text: "Studying new functions capable of better expressing the property's potential, in compliance with applicable planning and building regulations.", img: IMG.cambioUso, alt: "Building with a new designated use" },
    ],
    conclusion: "Every transformation starts from the same question: what is the best possible use of this property?",
    cta: "Request an assessment",
  },
};

export default function Valorizzazione() {
  const { lang } = useLang();
  const t = copy[lang];
  useSEO({ title: t.seoTitle, description: t.seoDesc, path: "/valorizzazione", lang });

  return (
    <div data-testid="valorizzazione-page">
      <PageHero kicker={t.kicker} lines={t.lines} sub={t.sub} image={IMG.hero} imageAlt={lang === "it" ? "Soggiorno contemporaneo ristrutturato" : "Renovated contemporary living room"} />

      <section className="mx-auto max-w-7xl px-6 py-24 md:px-10 md:py-36">
        <FadeIn>
          <SectionLabel>{t.label}</SectionLabel>
        </FadeIn>
        <div className="mt-16 space-y-24 md:mt-24 md:space-y-32">
          {t.areas.map((a, i) => (
            <div key={a.n} className="grid gap-10 md:grid-cols-12 md:items-center md:gap-16">
              <FadeIn className={`md:col-span-6 ${i % 2 === 1 ? "md:order-2" : ""}`}>
                <ParallaxImage src={a.img} alt={a.alt} className="aspect-[4/3] w-full" />
              </FadeIn>
              <FadeIn className={`md:col-span-5 ${i % 2 === 1 ? "md:order-1 md:col-start-1" : "md:col-start-8"}`} delay={0.12}>
                <div className="font-serif text-6xl font-light text-ink/10 md:text-7xl">{a.n}</div>
                <h2 className="mt-3 font-serif text-3xl font-light tracking-tight md:text-5xl">{a.title}</h2>
                <p className="mt-6 max-w-md text-base leading-relaxed text-stone">{a.text}</p>
              </FadeIn>
            </div>
          ))}
        </div>
      </section>

      <section className="bg-ink text-ivory">
        <div className="mx-auto max-w-5xl px-6 py-24 text-center md:px-10 md:py-36">
          <FadeIn>
            <p className="mx-auto max-w-3xl font-serif text-3xl font-light italic leading-snug md:text-5xl">
              “{t.conclusion}”
            </p>
            <div className="mt-12 flex justify-center">
              <CTAButton to="/contatti" variant="light" testId="valorizzazione-cta">
                {t.cta}
              </CTAButton>
            </div>
          </FadeIn>
        </div>
      </section>
    </div>
  );
}
